























































































































































































































































































import { Component, Mixins, Watch } from "vue-property-decorator";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import CountriesStore from "@/mixins/constants/countriesStore";
import RegionsStore from "@/mixins/constants/regionsStore";
import LanguagesStore from "@/mixins/constants/languagesStore";
import HasValidation from "@/mixins/HasValidation";
import BrandMixin from "@/mixins/brandMixin";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ImageObjectTypes, ImageSubTypes } from "@/models/images";
import { ImageUploadTypes } from "@/data/constants";

// Types
import { IUploadFile } from "@/models/uploadFiles";
import { IBrand, IBrandSettingsForm } from "@/models/brands";
import { IGoogleFont } from "@/models/google";
import { ICountry, ILanguage, IRegion } from "@/models/constants";
import _ from "@/boot/lodash";

@Component({
  components: {
    "brand-languages": () =>
      import("@/components/app/admin/settings/brands/brandLanguages.vue"),
    "file-upload-field": () =>
      import("@/components/app/global/fields/fileUploadField.vue"),
    "font-grid-item": () =>
      import("@/components/app/admin/settings/brands/fonts/fontGridItem.vue"),
    "wipe-data-checkbox": () =>
      import("@/components/app/admin/settings/brands/wipeDataCheckbox.vue")
  }
})
export default class BrandSettings extends Mixins(
  RequestPromises,
  CountriesStore,
  RegionsStore,
  LanguagesStore,
  HasValidation,
  BrandMixin
) {
  // Data

  public form = {} as IBrandSettingsForm;
  public formClone = {} as IBrandSettingsForm;

  public isLoading = false;
  public isReloading = false;
  public requestError = false;
  public isProcessing = false;

  // Brand Imaginery

  public imageUploadTypes = ImageUploadTypes;

  public images = {
    logo: {
      file: {} as IUploadFile,
      getValue: (brand: IBrand) => {
        return brand?.image?.hash || null;
      },
      type: ImageObjectTypes.BRAND,
      subtype: ImageSubTypes.IMAGE,
      label: this.$t("_.brand_logo"),
      isDisabled:
        this.isProcessing ||
        !(
          this.$userCan("upload_brand_default_image") &&
          this.$userCan("delete_brand_image")
        ),
      uploadPayload: { isDefault: true }
    },
    icon: {
      file: {} as IUploadFile,
      getValue: (brand: IBrand) => brand?.icon?.hash || null,
      type: ImageObjectTypes.BRAND,
      subtype: ImageSubTypes.ICON,
      label: this.$t("_.brand_logomark"),
      isDisabled:
        this.isProcessing || !this.$userCan("upload_brand_default_image"),
      uploadPayload: { isDefault: true }
    },
    favicon: {
      file: {} as IUploadFile,
      getValue: (brand: IBrand) => brand.favicon?.hash || null,
      type: ImageObjectTypes.BRAND_FAVICON,
      subtype: ImageSubTypes.IMAGE,
      label: this.$t("_.brand_favicon"),
      isDisabled: this.isProcessing || !this.$userCan("upload_brand_favicon")
    },
    email_logo: {
      file: {} as IUploadFile,
      getValue: (brand: IBrand) => brand.email_logo?.hash || null,
      type: ImageObjectTypes.BRAND_EMAIL_LOGO,
      subtype: ImageSubTypes.IMAGE,
      label: this.$t("_.email_logo"),
      isDisabled: this.isProcessing || !this.$userCan("upload_brand_email_logo")
    }
  };

  // Methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.isLoading = true;
    this.loadData();
  }

  public async loadData() {
    try {
      await Promise.all([
        this.getBrand(),
        this.getCountries(),
        this.getLanguages()
      ]);
      this.fillForm();
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
      this.addBrandName();
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getBrand() {
    return this.$store.dispatch("brand/get", { ignoreStored: true });
  }

  public fillForm() {
    this.form = _.merge(
      {
        region_id: null
      },
      _.pick(this.brand, [
        "code",
        "country_id",
        "language_id",
        "name",
        "region_id",
        "style"
      ]),
      {
        wipe_data: !!this.brand.wipe_data,
        supported_languages: _.orderBy(
          _.map(_.get(this.brand, "languages", []), language => language.id)
        )
      }
    ) as IBrandSettingsForm;

    _.keys(this.images).forEach(field => {
      this.$set(this.form, field, this.images[field].getValue(this.brand));
    });

    this.formClone = _.cloneDeep(this.form);
  }

  public addBrandName() {
    this.$store.commit("ui/breadcrumb/add", {
      key: "adminBrand",
      value: this.brand.name
    });
  }

  public refreshBrand() {
    return this.$store.dispatch("brand/get", { ignoreStored: true });
  }

  public openSelectGoogleFont() {
    this.$store.dispatch(`brand/openSelectFontModal`, {
      events: {
        select: (font: IGoogleFont) => {
          this.setBrandFont(font);
        }
      }
    });
  }

  public setBrandFont(font?: IGoogleFont) {
    // Set style object
    if (!this.form.style) this.$set(this.form, "style", {});
    if (font) {
      this.$set(this.form.style as object, "brand_font", {
        family: font?.family,
        version: font?.version
      });
    } else {
      this.$set(this.form.style as object, "brand_font", undefined);
    }
  }

  public get isFormChanged() {
    return (
      !_.isEqual(
        _.omit(this.form, ["supported_languages"]),
        _.omit(this.formClone, ["supported_languages"])
      ) ||
      _.sortBy(this.form.supported_languages).join("_") !==
        _.sortBy(this.formClone.supported_languages).join("_")
    );
  }

  public get formIsDisabled() {
    return (
      this.isProcessing ||
      !this.isFormChanged ||
      this.hasErrors ||
      this.isLoading
    );
  }

  public get canUpdateBrand() {
    return this.$userCan("update_brand");
  }

  public get canSelectAllLangs() {
    return this.form.supported_languages?.length < this.languages?.length;
  }

  public get country(): ICountry | undefined {
    const country_id = this.form.country_id || this.brand?.country_id;
    if (!country_id) return undefined;
    return this.$_.find(this.countries, ["id", country_id]);
  }

  public get countryRegions(): IRegion[] | undefined {
    if (!this.country?.code) return [];
    return this.regions?.[this.country.code];
  }

  public get hasBrandFont() {
    return !!this.form?.style?.brand_font;
  }

  public get brandFont() {
    return {
      family: this.form?.style?.brand_font?.family || "Quicksand"
    };
  }

  public get supportedLanguages() {
    return _.orderBy(
      _.map(this.form.supported_languages, id => {
        return _.find(this.languages, ["id", id]);
      }),
      ["language"],
      ["asc"]
    ) as ILanguage[];
  }

  public revertToInitialState() {
    this.form = _.cloneDeep(this.formClone);
  }

  public async validateBeforeSubmit() {
    if (this.canUpdateBrand) {
      this.isProcessing = true;

      try {
        if (!(await this.validate())) {
          throw this.$t("_sentence.form_is_not_valid");
        }

        await Promise.all(
          _.keys(this.images).map(key => this.manageImage(key))
        );

        await this.updateBrand();

        await Promise.all([
          this.refreshBrand(),
          // Here we refresh user data to cover brand object changes (eg. name)
          this.$store.dispatch("auth/admin/getUser")
        ]);
        this.fillForm();

        this.$store.dispatch("toast/show", {
          message: this.$t("_.object_successfully_updated", {
            object: this.$t("_.brand")
          })
        });
      } catch (error) {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      } finally {
        this.isProcessing = false;
      }
    }
  }

  public toggleAllLanguages() {
    this.form.supported_languages = this.canSelectAllLangs
      ? _.map(this.languages, "id")
      : [this.form.language_id];
  }

  public updateBrand() {
    return this.$store.dispatch(`data/${DataModules.BRANDS}/update`, {
      id: this.brandId,
      data: this.getNewValues()
    });
  }

  public manageImage(field: string) {
    if (this.form[field] === this.formClone[field]) return Promise.resolve();
    if (this.form[field] === null) return this.deleteImage(field);
    return this.uploadImage(field);
  }

  public async uploadImage(field: string) {
    const data = new FormData();
    data.append("image", _.get(this.images[field].file, "file.fileObj"));
    data.append("type", this.images[field].subtype || ImageSubTypes.IMAGE);

    try {
      const result = await this.$store.dispatch(
        `data/${DataModules.IMAGES}/uploadImage`,
        {
          typeId: this.brandId,
          imageType: this.images[field].type,
          ...(this.images[field].uploadPayload || {}),
          data,
          onUploadProgress: (progressEvent: {
            loaded: number;
            total: number;
          }) => {
            this.images[field].file = _.merge(this.images[field].file, {
              progress: Math.floor(
                (progressEvent.loaded * 100) / progressEvent.total
              ),
              isUploading: true
            });
          },
          vm: this
        }
      );

      this.images[field].file = _.merge(this.images[field].file, {
        progress: 0,
        isUploading: false,
        hash: result.value
      });
      return Promise.resolve();
    } catch (error) {
      const data = _.get(error, "response.data.error.data", {});
      if (data?.image) {
        data[field] = data.image;
        delete data.image;
      }
      throw error;
    }
  }

  public deleteImage(field: string) {
    return this.$store.dispatch(`data/${DataModules.IMAGES}/deleteImage`, {
      typeId: this.brandId,
      imageType: this.images[field].type,
      hash: this.formClone[field],
      vm: this
    });
  }

  public getNewValues() {
    const newValues: any = _.omitBy(this.form, (v, k) => {
      return this.formClone[k] === v;
    });

    delete newValues.logo;
    delete newValues.favicon;
    delete newValues.email_logo;

    return newValues;
  }

  public imageDeleted(field: string) {
    this.images[field].file = {} as IUploadFile;
  }

  public imageAdded(field: string, image: any) {
    this.images[field].file = {
      file: image,
      isUploading: false,
      progress: 0,
      hash: null
    } as IUploadFile;
  }

  public onInput(value, field) {
    this.$set(this.form, field, value);
  }

  public guardFunctionalities = ["get_brand"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  @Watch("country", { deep: true })
  public onCountryChange({ id: countryId, code: countryCode }: ICountry) {
    // Clear form region if country is different from current brand value
    if (countryId && countryId !== this.brand?.country_id)
      this.form.region_id = null;
    // Reset form region if country reverts back to current brand value
    if (countryId && countryId === this.brand?.country_id)
      this.form.region_id = this.brand?.region_id || null;
    // Load regions for new country selection
    this.regionsStoreLoadData({ countryId, countryCode });
  }
}
